import {
  Box,
  Button,
  Divider,
  Fade,
  Paper,
  Popper,
  Stack,
  styled,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import {
  DateCalendar,
  LocalizationProvider,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import moment from "moment";
import { DateRange } from "models/interfaces";
import {
  getDateInterval,
  isTruthy,
  last30DaysFromDate,
  last30DaysToDate,
  openErrorNotification,
} from "helpers/methods";
import strings from "global/constants/StringConstants";
import ClearIcon from "@mui/icons-material/Clear";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { isWithinInterval, isSameDay, format } from "date-fns";
import { selectCampaignRetentionDuration } from "redux/generalSlice";
import { useAppSelector } from "utils/hooks";

const StyledPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "isBetween" && prop !== "isStart" && prop !== "isEnd",
})(({ theme, isBetween, isStart, isEnd }: any) => ({
  ...(isStart && {
    backgroundColor: "#7551D0",
    color: "#fff",
    borderRadius: "0px !important",
  }),
  ...(isEnd && {
    backgroundColor: "#7551D0",
    color: "#fff",
    borderRadius: "0px !important",
  }),
  ...(isBetween && {
    backgroundColor: "#f4f1fb",
    color: "#000",
    borderRadius: "0",
  }),
}));

interface Props {
  openDateRangePicker: boolean;
  setOpenDateRangePicker: (value: boolean) => void;
  dateRange: DateRange;
  setDateRange: (dateRange: DateRange) => void;
  helperFunction?: any;
  disabled?: boolean;
}

const DashboardDateRangePicker: FC<Props> = ({
  openDateRangePicker,
  setOpenDateRangePicker,
  dateRange,
  setDateRange,
  helperFunction,
  disabled,
}) => {
  const disabledDatePicker = disabled ?? false;
  const anchorEl = React.useRef(null);
  const currentDate = new Date();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const [isDateApplied, setIsDateApplied] = useState<boolean>(false);
  const campaignRetention = useAppSelector(selectCampaignRetentionDuration);
  const campaignRetentionDates = new Date();
  campaignRetentionDates.setMonth(currentDate.getMonth() - campaignRetention);
  const [tempDateRange, setTempDateRange] = useState<any>(dateRange);

  useEffect(() => {
    if (openDateRangePicker) {
      setTempDateRange(dateRange);
    }
  }, [openDateRangePicker]);

  const handleDateRangeChange = (newValue: Date, name: string) => {
    setDateRange({
      ...dateRange,
      [name]: moment(newValue).format("YYYY-MM-DD"),
    });
  };

  const setDateInterval = (filter: string) => {
    const range = getDateInterval(filter, dateRange);

    setDateRange({
      fromDate:
        range.fromDate !== null
          ? moment(range.fromDate).format("YYYY-MM-DD")
          : last30DaysFromDate,
      toDate:
        range.toDate !== null
          ? moment(range.toDate).format("YYYY-MM-DD")
          : last30DaysToDate,
    });
  };

  const CustomDay = (props: any) => {
    const { day, selected } = props;
    const { fromDate, toDate } = dateRange;
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    const isStart = from && isSameDay(day, from);
    const isEnd = to && isSameDay(day, to);
    const isBetween =
      from && to && isWithinInterval(day, { start: from, end: to });

    return (
      <StyledPickersDay
        {...props}
        isStart={isStart}
        isEnd={isEnd}
        isBetween={isBetween}
        selected={selected}
      />
    );
  };
  const dateRangePicker = () => {
    const filteredDateRangeFilters =
      campaignRetention === 1
        ? strings.DATE_RANGE_FILTERS.filter(
            (filter) => filter !== strings.previousMonth
          )
        : strings.DATE_RANGE_FILTERS;
    return (
      <Stack direction={isMobile ? "column" : "row"} spacing={1} mt={1}>
        <Stack
          direction={"column"}
          spacing={1}
          mt={2}
          sx={{
            margin: "12px 15px 0 8px !important",
          }}
        >
          {filteredDateRangeFilters
            .filter((item) => item !== strings.allTime)
            .map((filter: any) => {
              return (
                <Typography
                  variant="body1"
                  p={1}
                  sx={{
                    borderRadius: "8px",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#7551D0",
                      color: "#FFF",
                    },
                  }}
                  onClick={() => setDateInterval(filter)}
                >
                  {filter}
                </Typography>
              );
            })}
        </Stack>
        <Box>
          <Divider orientation="vertical" flexItem sx={{ height: "100%" }} />
        </Box>
        <Stack direction={"column"}>
          <Stack
            display={"flex"}
            direction={isMobile ? "column" : "row"}
            justifyContent={"space-between"}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={dateRange.fromDate ? new Date(dateRange.fromDate) : null}
                onChange={(newDate) =>
                  handleDateRangeChange(newDate, "fromDate")
                }
                slots={{ day: CustomDay }}
                sx={{
                  "& .MuiPickersDay-root:not(.Mui-selected)": {
                    border: "none",
                  },
                }}
                disableFuture
                minDate={campaignRetentionDates}
                maxDate={
                  dateRange.toDate
                    ? new Date(dateRange.toDate)
                    : moment().toDate()
                }
              />

              <DateCalendar
                value={dateRange.toDate ? new Date(dateRange.toDate) : null}
                onChange={(newDate) => handleDateRangeChange(newDate, "toDate")}
                slots={{ day: CustomDay }}
                disableFuture
                minDate={
                  dateRange.fromDate ? new Date(dateRange.fromDate) : null
                }
                maxDate={moment().toDate()}
                sx={{
                  "& .MuiPickersDay-root:not(.Mui-selected)": {
                    border: "none",
                  },
                }}
              />
            </LocalizationProvider>
          </Stack>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "right",
            }}
            mr={2}
            pb={1}
          >
            <Stack direction={"row"} spacing={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setIsDateApplied(false);
                  setDateRange(tempDateRange);
                  setOpenDateRangePicker(false);
                }}
              >
                Close
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  if (!isTruthy(dateRange.fromDate)) {
                    openErrorNotification("Please select From Date.");
                    return;
                  }
                  if (!isTruthy(dateRange.toDate)) {
                    openErrorNotification("Please select To Date.");
                    return;
                  }
                  setTempDateRange(dateRange);
                  setIsDateApplied(!isDateApplied);
                  setOpenDateRangePicker(false);
                  helperFunction(dateRange);
                }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    );
  };

  const getClearIcon = () => {
    if (
      dateRange.fromDate === last30DaysFromDate &&
      dateRange.toDate === last30DaysToDate
    ) {
      return null;
    } else {
      return (
        <Tooltip title={"Clear"} placement="right" arrow>
          <ClearIcon
            fontSize="small"
            htmlColor="grey"
            sx={{ cursor: "pointer" }}
            onClick={(e: any) => {
              e.stopPropagation();
              setDateRange({
                fromDate: last30DaysFromDate,
                toDate: last30DaysToDate,
              });
              const tempDateRange = {
                fromDate: last30DaysFromDate,
                toDate: last30DaysToDate,
              };
              helperFunction(tempDateRange);
              setOpenDateRangePicker(false);
            }}
          />
        </Tooltip>
      );
    }
  };

  const getDateValue = () => {
    if (
      dateRange.fromDate === last30DaysFromDate &&
      dateRange.toDate === last30DaysToDate
    ) {
      return "Last 30 Days";
    } else {
      return `${dateRange.fromDate} to ${dateRange.toDate}`;
    }
  };

  return (
    <Box>
      <Stack direction={"row"} spacing={2} alignItems={"center"}>
        <TextField
          disabled={disabledDatePicker}
          ref={anchorEl}
          variant="outlined"
          value={getDateValue()}
          onClick={(e: any) => {
            e.stopPropagation();
            setOpenDateRangePicker(!openDateRangePicker);
          }}
          sx={{
            "& .MuiOutlinedInput-input": {
              textAlign: "right",
              marginRight: "8px",
              cursor: "pointer",
            },
            "& .MuiInputBase-input": {
              cursor: "pointer",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: 0,
            },
            "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
              border: 0,
            },
            "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
              {
                border: 0,
              },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#fff",
              },
              "&:hover fieldset": {
                borderColor: "#fff",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#fff",
              },
            },
          }}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <Stack direction="row" spacing={1}>
                {getClearIcon()}
                <DateRangeIcon fontSize="small" sx={{ cursor: "pointer" }} />
              </Stack>
            ),
          }}
        />
      </Stack>
      <Popper
        sx={{
          zIndex: 1300,
          boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
          borderRadius: "10px",
          background: "#fff",
        }}
        open={openDateRangePicker}
        anchorEl={anchorEl.current}
        placement="bottom"
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps}>
            <Paper
              sx={{
                borderRadius: "10px",
                overflow: "hidden",
                padding: "10px",
                backgroundColor: "#fff",
              }}
            >
              {dateRangePicker()}
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default DashboardDateRangePicker;
